import { ReactNode } from "react";

import {
  AuditOutlined,
  ExportOutlined,
  QuestionOutlined,
  QuestionCircleOutlined,
  SwapOutlined,
  ApiOutlined,
  SettingOutlined,
  CloudServerOutlined,
  UserOutlined,
  ApartmentOutlined,
  AccountBookOutlined,
  BarcodeOutlined,
  FileImageOutlined,
  UnorderedListOutlined,
  InfoCircleOutlined,
  CameraOutlined,
  GroupOutlined,
  FieldTimeOutlined,
} from "@ant-design/icons";

import themes from "devextreme/ui/themes";

import {
  IAppUtil,
  AppProps,
  AppRoute,
  EzhouAppStateBase,
  AppThemeMode,
  EzhouFormProps,
  PageProps,
} from "@ezhou/core";
import { LocalizationProvider, AdapterDateFns } from "@ezhou/core-component";
import { EzhouApp, EzhouAppUtil, DefaultRedirect } from "@ezhou/app_mantis_1_1";

import { UserProfile } from "./model/userProfile";

import { ServiceAdminRole } from "./model/appConstants";
import { getUserProfile, NoExistingOrganizationId } from "./services/userSettingService";
import { getCurrentOrganizationLicenseState, LicenseState } from "./services/licenseService";
import LicenseExpiringAlert from "./components/license/LicenseExpiringAlert";

import WelcomePage from "./pages/Welcome";
import JeExportByPostingDate from "./pages/exporting/JeExportByPostingDate";
import OneStepJeExportByPostingDate from "./pages/exporting/OneStepJeExportByPostingDate";
import Register from "./pages/login/Register";
import Help from "./pages/help/Help";
import Organizations from "./pages/serviceAdmin/Organizations";
import Customers from "./pages/serviceAdmin/Customers";
import Users from "./pages/serviceAdmin/Users";
import UnderConstruction from "./pages/UnderConstruction";
import Accounts from "./pages/admin/Accounts";
import MappingDimensions from "./pages/admin/MappingDimensions";
import JournalEntryTypeMappings from "./pages/mapping/JournalEntryTypeMappings";
import { AffixMappings } from "./pages/mapping/AffixMappings";
import { MappingConfigVersions } from "./pages/mapping/MappingConfigVersions";
import { GroupRuleMappings } from "./pages/mapping/GroupRuleMappings";
import { AccountingDateRuleMappings } from "./pages/mapping/AccountingDateRuleMappings";
import { ReferenceMappings } from "./pages/mapping/ReferenceMappings";
import TessituraSettings from "./pages/admin/TessituraSettings";
import RestfulApiResources from "./pages/admin/RestfulApiResources";
import UserSettings from "./pages/admin/UserSettings";
import JournalEntryExportSettings from "./pages/admin/JournalEntryExportSettings";
import MappingRuleTypeSettings from "./pages/admin/MappingRuleTypeSettings";

// icons
const icons = {
  AuditOutlined,
  ExportOutlined,
  QuestionOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  SwapOutlined,
  ApiOutlined,
  CloudServerOutlined,
  UserOutlined,
  ApartmentOutlined,
  AccountBookOutlined,
  BarcodeOutlined,
  FileImageOutlined,
  UnorderedListOutlined,
  InfoCircleOutlined,
  CameraOutlined,
  GroupOutlined,
  FieldTimeOutlined,
};

class AppUtil extends EzhouAppUtil {
  getRouteComponent(route: AppRoute, pageProps: EzhouFormProps): ReactNode {
    switch (route.key) {
      case "root":
        return <DefaultRedirect {...pageProps} />;
      case "oneStepJeExportByPostingDate":
        return <OneStepJeExportByPostingDate {...pageProps} />;
      case "jeExportByPostingDate":
        return <JeExportByPostingDate {...pageProps} />;
      case "help":
        return <Help {...pageProps} />;
      case "restfulResources":
        return <RestfulApiResources {...pageProps} />;
      case "tessituraInterfaceSettings":
        return <TessituraSettings {...pageProps} />;
      case "organizationUsers":
        return <UnderConstruction {...pageProps} />;
      case "accounts":
        return <Accounts {...pageProps} />;
      case "mappingDimensions":
        return <MappingDimensions {...pageProps} />;
      case "journalEntryTypeMappings":
        return <JournalEntryTypeMappings {...pageProps} />;
      case "affixMappings":
        return <AffixMappings {...pageProps} />;
      case "groupRuleMappings":
        return <GroupRuleMappings {...pageProps} />;
      case "accountingDateRuleMappings":
        return <AccountingDateRuleMappings {...pageProps} />;
      case "referenceMappings":
        return <ReferenceMappings {...pageProps} />;
      case "mappingConfigVersions":
        return <MappingConfigVersions {...pageProps} />;
      case "journalEntryExportSettings":
        return <JournalEntryExportSettings {...pageProps} />;
      case "mappingRuleTypeSettings":
        return <MappingRuleTypeSettings {...pageProps} />;
      case "users":
        return <Users {...pageProps} />;
      case "organizations":
        return <Organizations {...pageProps} />;
      case "customers":
        return <Customers {...pageProps} />;
      case "userSettings":
        return <UserSettings {...pageProps} />;
      default:
        return <WelcomePage {...pageProps} />;
    }
  }

  getLoginRouteElement(path: string, pageProps: PageProps): ReactNode | null {
    if (path === "register") {
      return <Register />;
    }
    return null;
  }

  getMenuIcon(key: string): any {
    switch (key) {
      case "help":
        return icons.QuestionCircleOutlined;
      case "oneStepJeExportByPostingDate":
        return icons.ExportOutlined;
      case "jeExportByPostingDate":
        return icons.AuditOutlined;
      case "adminGroupMenu":
        return icons.SettingOutlined;
      case "restfulResources":
        return icons.SwapOutlined;
      case "tessituraInterfaceSettings":
        return icons.ApiOutlined;
      case "organizationUsers":
        return icons.UserOutlined;
      case "accounts":
        return icons.AccountBookOutlined;
      case "mappingDimensions":
        return icons.BarcodeOutlined;
      case "mappingRuleGroupMenu":
        return icons.FileImageOutlined;
      case "journalEntryTypeMappings":
        return icons.UnorderedListOutlined;
      case "affixMappings":
        return icons.InfoCircleOutlined;
      case "groupRuleMappings":
        return icons.GroupOutlined;
      case "accountingDateRuleMappings":
        return icons.FieldTimeOutlined;
      case "referenceMappings":
        return icons.SwapOutlined;
      case "mappingConfigVersions":
        return icons.CameraOutlined;
      case "journalEntryExportSettings":
        return icons.ExportOutlined;
      case "mappingRuleTypeSettings":
        return icons.SettingOutlined;
      case "serviceAdminGroupMenu":
        return icons.CloudServerOutlined;
      case "users":
        return icons.UserOutlined;
      case "organizations":
        return icons.ApartmentOutlined;
      case "customers":
        return icons.UserOutlined;
      default:
        return icons.QuestionOutlined;
    }
  }

  setThemeMode(mode: AppThemeMode): void {
    themes.current(mode === "light" ? "material.blue.light" : "material.blue.dark");
  }
}

class AppState extends EzhouAppStateBase { }

class AppClass extends EzhouApp<AppState> {
  private licenseState: LicenseState = { status: "ok" };

  constructor(props: AppProps) {
    super(props, null);
    this.handleWarmupData = this.handleWarmupData.bind(this);
    this.handleWarmupError = this.handleWarmupError.bind(this);
    if (props.config.getSection<boolean>("warmupService")) {
      this.warmupService();
    }
  }

  private warmupService(): void {
    this.logger.debug("Starting to wWarmup service");
    this.appUtil.httpClients["eAccountingService"]
      .get("system-info/get-all", this.handleWarmupError)
      .then((data) => {
        this.handleWarmupData(data, "eAccountingService");
      });
    this.appUtil.httpClients["authService"]
      .get(".well-known/openid-configuration", this.handleWarmupError)
      .then((data) => {
        this.handleWarmupData(data, "authService");
      });
  }

  private handleWarmupError(err: any): void {
    this.logger.info("Error in eAccountingService warm up");
    console.log(err);
  }

  private handleWarmupData(data: unknown, serviceName: string): void {
    this.logger.debug(`${serviceName} warm up done!`);
    if (data) {
      if (typeof data === "object") {
        this.logger.debug(JSON.stringify(data));
      } else {
        this.logger.debug(data.toString());
      }
    } else {
      this.logger.debug("No data");
    }
  }

  protected getAppUtil(): IAppUtil {
    const appUtil = new AppUtil(this, this.loggerProvider.getLogger("AppUtil"));
    return appUtil;
  }

  private addOrganizationInfoToUserProfileMenu(
    currentUserProfile: UserProfile,
    apiVersion?: { version: string; buildDateTime: string }
  ) {
    if (apiVersion) {
      this.setApiVersionInfo(apiVersion);
    }

    if (currentUserProfile.defaultOrganization === NoExistingOrganizationId) {
      this.setUserProfileMenuItems([
        {
          name: `No current organization`,
        },
      ]);
    }
    else {
      this.setUserProfileMenuItems([
        {
          name: currentUserProfile.defaultOrganizationInfo.fullName
        },
        {
          name: "Change Working Organization",
          path: "/userSettings",
        },
      ]);
    }
  }

  initializationExceptionHandler = (error: any) => {
    this.logger.error(`Http Request Error: ${error.message}`);
    this.logger.error(`Request url: ${error.config.url}`);
    if (error.response) {
      this.logger.error(`Response status code: ${error.response.status}`);
      this.logger.error(
        `Response data: ${error.response.data ? JSON.stringify(error.response.data) : ""}`,
      );
      this.logger.error(
        `Response response: ${error.response.response ? JSON.stringify(error.response.response) : ""
        }`,
      );
      this.logger.error(
        `Response text: ${error.response.responseText ? JSON.stringify(error.response.responseText) : ""
        }`,
      );
    }
    throw error;
  };

  async initializeAfterLogin(): Promise<void> {
    const httpClient = this.appUtil.httpClients["eAccountingService"];
    const [currentUserProfile, apiVersion] = await Promise.all([
      getUserProfile(this.dataStore, httpClient, this.initializationExceptionHandler),
      httpClient
        .get<{ version: string; buildDateTime: string }>("api-information/version", () => {})
        .catch(() => undefined),
    ]);
    this.addOrganizationInfoToUserProfileMenu(currentUserProfile, apiVersion);
    this.licenseState = getCurrentOrganizationLicenseState(this.dataStore);
    if (this.licenseState.status === "notFound") {
      const isServiceAdmin = currentUserProfile.roles?.includes(ServiceAdminRole) ?? false;
      if (!isServiceAdmin) {
        throw new Error("License cannot be found for the current organization.");
      }
    }
    if (this.licenseState.status === "expired") {
      const organizationName = this.licenseState.organizationName ?? "the current organization";
      throw new Error(`The license for ${organizationName} has expired.`);
    }
    this.updateAppState();
    return;
  }

  render() {
    this.licenseState = getCurrentOrganizationLicenseState(this.dataStore);
    return (
      <>
        {super.render()}
        {this.licenseState.status === "expiringSoon" && (
          <LicenseExpiringAlert
            key={this.licenseState.organizationName}
            licenseState={this.licenseState}
          />
        )}
      </>
    );
  }
}

export function App(props: AppProps) {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <AppClass {...props} />
    </LocalizationProvider>
  );
}
